import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { X, Search, MapPin, CheckCircle, Loader2, Building2 } from 'lucide-react';
import { clubsService } from '@/services/api/clubs.service';
import { coachAffiliationsService } from '@/services/domains/coaching/coach-affiliations.service';
import { useCoachDashboard } from '@/hooks/useCoachDashboard';
import { useAppTranslations } from '@/hooks/useAppTranslations';
import { useCoachAffiliations } from '@/hooks/useCoachAffiliations';
import { ClubAvatar } from '@/layouts/CoachLayout';
import { toast } from 'sonner';
import type { Club } from '@/services/api/clubs.service';

// ── Design tokens (copied from StudentDetailPanel) ────────────────────────────
const S = {
  panel: {
    background: '#FFFFFF',
    border: '1px solid rgba(15,23,42,0.08)',
    boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, -20px 0 60px rgba(15,23,42,0.12), 0 32px 64px rgba(15,23,42,0.10)',
  } as React.CSSProperties,
  actionTile: {
    background: '#FFFFFF',
    border: '1px solid rgba(15,23,42,0.08)',
    boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset, 0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.05)',
  } as React.CSSProperties,
  primaryCTA: {
    background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)',
    boxShadow: '0 1px 0 rgba(255,255,255,0.20) inset, 0 4px 14px rgba(42,68,148,0.28)',
  } as React.CSSProperties,
};

interface SearchClubsPanelProps {
  open: boolean;
  onClose: () => void;
}

export const SearchClubsPanel: React.FC<SearchClubsPanelProps> = ({ open, onClose }) => {
  const { profile } = useCoachDashboard();
  const { affiliations } = useCoachAffiliations();
  const { t } = useAppTranslations();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [clubs, setClubs] = useState<Club[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [requestingId, setRequestingId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const affiliatedIds = new Set(
    affiliations.filter(a => a.status !== 'left').map(a => a.clubId)
  );

  // Focus search on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 60);
      // Load initial list
      doSearch('');
    }
  }, [open]);

  const doSearch = async (q: string) => {
    setIsLoading(true);
    try {
      const res = await clubsService.searchClubs({ search: q || undefined, limit: 50 });
      setClubs(res.data || []);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 300);
  };

  const handleRequest = async (clubId: string) => {
    if (!profile?.id) {
      toast.error(t.coachAffiliations.noCoachProfileError);
      return;
    }
    setRequestingId(clubId);
    try {
      const res = await coachAffiliationsService.requestToJoinClub(clubId, profile.id);
      if (res.success) {
        queryClient.invalidateQueries({ queryKey: ['coachAffiliations'] });
        toast.success(t.coachAffiliations.requestSentSuccess);
      } else {
        toast.error(res.error || t.coachAffiliations.requestFailedError);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t.coachAffiliations.requestFailedError);
    } finally {
      setRequestingId(null);
    }
  };

  if (!open) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[59]" onClick={onClose} />

      {/* Panel */}
      <div
        className="fixed right-0 sm:right-6 top-0 sm:top-6 bottom-0 sm:bottom-6 w-full sm:w-[420px] bg-white rounded-none sm:rounded-2xl flex flex-col z-[60] overflow-hidden"
        style={S.panel}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="px-6 flex-shrink-0" style={{ borderBottom: '1px solid rgba(15,23,42,0.07)', paddingTop: 'calc(1.25rem + env(safe-area-inset-top, 0px))', paddingBottom: '1.25rem' }}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'rgba(15,23,42,0.35)' }}>
                {t.coach.search.eyebrow}
              </p>
              <p className="text-[17px] font-bold mt-1 truncate" style={{ color: '#0F172A' }}>
                {t.coach.search.title}
              </p>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 flex items-center justify-center transition-colors"
              style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(15,23,42,0.06)', border: '1px solid rgba(15,23,42,0.08)' }}
            >
              <X className="w-4 h-4" style={{ color: '#64748B' }} />
            </button>
          </div>

          {/* Search input inside header — always visible */}
          <div className="relative mt-4">
            <Search
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
              style={{ color: 'rgba(15,23,42,0.30)' }}
            />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleQueryChange}
              placeholder={t.coach.search.placeholder}
              className="w-full pl-10 pr-4 py-2.5 text-[13px] font-medium focus:outline-none transition-all"
              style={{
                background: '#F8FAFC',
                border: '1px solid rgba(15,23,42,0.10)',
                borderRadius: 12,
                color: '#0F172A',
              }}
              onFocus={e => {
                e.currentTarget.style.border = '1px solid rgba(42,68,148,0.35)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(42,68,148,0.08)';
              }}
              onBlur={e => {
                e.currentTarget.style.border = '1px solid rgba(15,23,42,0.10)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto" style={{ background: '#F8FAFC' }}>
          {isLoading ? (
            <div className="px-5 py-5 space-y-2">
              {[1, 2, 3].map(i => (
                <div
                  key={i}
                  className="rounded-2xl animate-pulse"
                  style={{ height: 68, background: 'rgba(15,23,42,0.06)' }}
                />
              ))}
            </div>
          ) : clubs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
                style={{ background: 'rgba(15,23,42,0.05)', border: '1px solid rgba(15,23,42,0.08)' }}
              >
                <Building2 className="w-6 h-6" style={{ color: 'rgba(15,23,42,0.25)' }} />
              </div>
              <p className="text-[13px] font-semibold mb-1" style={{ color: '#0F172A' }}>{t.coach.search.noClubsFound}</p>
              <p className="text-[12px] leading-relaxed" style={{ color: '#94A3B8' }}>
                {query ? t.coach.search.noResultsQuery : t.coach.search.startTyping}
              </p>
            </div>
          ) : (
            <div className="px-5 py-5 space-y-2">
              {clubs.map(club => {
                const affiliated = affiliatedIds.has(club.id);
                const requesting = requestingId === club.id;
                const location = [club.city, club.state, club.country].filter(Boolean).join(', ');

                return (
                  <div
                    key={club.id}
                    className="rounded-2xl px-4 py-3 flex items-center gap-3"
                    style={S.actionTile}
                  >
                    {/* Club avatar */}
                    <ClubAvatar
                      name={club.name ?? ''}
                      logoUrl={club.logo_url ?? null}
                      size={36}
                    />

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold truncate" style={{ color: '#0F172A' }}>
                        {club.name}
                      </p>
                      {location && (
                        <p className="flex items-center gap-1 text-[11px] mt-0.5" style={{ color: '#94A3B8' }}>
                          <MapPin className="w-2.5 h-2.5 flex-shrink-0" />
                          {location}
                        </p>
                      )}
                    </div>

                    {/* Action */}
                    {affiliated ? (
                      <span
                        className="flex-shrink-0 flex items-center gap-1 text-[11px] font-bold px-2.5 py-1"
                        style={{ background: '#D1FAE5', color: '#047857', borderRadius: 8 }}
                      >
                        <CheckCircle className="w-3 h-3" />
                        {t.coach.search.affiliated}
                      </span>
                    ) : (
                      <button
                        onClick={() => handleRequest(club.id)}
                        disabled={requesting}
                        className="flex-shrink-0 h-8 tap-target px-3 text-[12px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-60 transition-opacity"
                        style={{ ...S.primaryCTA, borderRadius: 10 }}
                      >
                        {requesting && <Loader2 className="w-3 h-3 animate-spin" />}
                        {t.coach.search.request}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div
          className="px-6 py-4 flex-shrink-0"
          style={{ borderTop: '1px solid rgba(15,23,42,0.07)' }}
        >
          <button
            onClick={onClose}
            className="w-full h-10 rounded-xl text-[13px] font-semibold transition-colors"
            style={{ background: 'rgba(15,23,42,0.06)', border: '1px solid rgba(15,23,42,0.08)', color: '#64748B', borderRadius: 12 }}
          >
            {t.coach.search.close}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};
